import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuthStore } from "@/stores/auth.store";

import { ProfileCreationForm } from "./index";

interface ProfileCheckProps {
  children: React.ReactNode;
  requireAuth?: boolean;
}

export default function ProfileCheck({ children, requireAuth = false }: ProfileCheckProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { curUser, loggedIn } = useAuthStore();
  const [willCreateProfile, setWillCreateProfile] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!loggedIn) {
      if (requireAuth) {
        navigate({ to: "/", replace: true });
      }
    } else if (!curUser?.profile) {
      setWillCreateProfile(true);
    } else {
      setWillCreateProfile(false);
    }

    setIsLoading(false);
  }, [loggedIn, curUser, requireAuth, navigate]);

  const handleProfileComplete = () => {
    setWillCreateProfile(false);
  };

  // Show loading while checking profile
  // TODO: update loading screen to follow the design system
  if (isLoading) {
    return (
      <div className="min-h-screen e-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t("profile.checking")}</p>
        </div>
      </div>
    );
  }

  // If user is not logged in, show children (normal app flow)
  if (!loggedIn) {
    return <>{children}</>;
  }

  // If user doesn't have a profile, show profile creation form
  if (willCreateProfile) {
    return <ProfileCreationForm onComplete={handleProfileComplete} />;
  }

  // If user has a profile, show normal app content
  return <>{children}</>;
}
